import React from "react";
import Alert from 'react-bootstrap/Alert';
import Button from 'react-bootstrap/Button';
import Container from 'react-bootstrap/Container';
import Form from 'react-bootstrap/Form';
import Modal from 'react-bootstrap/Modal';
import Table from 'react-bootstrap/Table';
import { Link } from 'react-router-dom';
import PortalPagination from '../../components/PortalPagination';
import PortalSpinner from '../../components/Spinner';
import PortalTableScroller from '../../components/PortalTableScroller';
import { partnersService } from '../../services/partners';
import { patientsService } from '../../services/patients';
import { canAccessPatients, canManagePatients } from '../../utils/access';
import { formatDateDisplay, formatDateForInput } from '../../utils/date';
import { getErrorMessage } from '../../utils/errorMessage';
import {
    filterNamedPartners,
    filterNamedPatients,
    getDuplicatePatientMessage,
    normalizeText
} from '../../utils/records';

const acesso = Number(sessionStorage.getItem("tipoAcesso") || 0);
const readOnlyMode = !canManagePatients(acesso);

class PartientManage extends React.Component {
    state = {
        datatable: [],
        baseData: [],
        partnerList: [],
        message: '',
        show: false,
        modalShow: false,
        loading: true,
        saving: false,
        sortBy: 'nome-asc',
        idPacientes: '',
        nome: '',
        datanascimento: '',
        rg: '',
        cpf: '',
        idParceiro: '',
        status: '',
        entrada: '',
        nomeFiltro: '',
        saida: '',
        currentPage: 1,
        postPerPage: 20
    };

    componentDidMount() {
        this.fetch();
    }

    showError = (error, fallbackMessage = 'Nao foi possivel concluir a operacao.') => {
        this.setState({ show: true, message: getErrorMessage(error, fallbackMessage) });
        setTimeout(() => { this.setState({ show: false }); }, 7000);
    };

    sortPatients = (items, sortBy = this.state.sortBy) => {
        const sortedItems = [...items];

        sortedItems.sort((a, b) => {
            switch (sortBy) {
                case 'nome-desc':
                    return `${b.nome || ''}`.localeCompare(`${a.nome || ''}`, 'pt-BR');
                case 'id-asc':
                    return Number(a.idPacientes) - Number(b.idPacientes);
                case 'id-desc':
                    return Number(b.idPacientes) - Number(a.idPacientes);
                case 'nome-asc':
                default:
                    return `${a.nome || ''}`.localeCompare(`${b.nome || ''}`, 'pt-BR');
            }
        });

        return sortedItems;
    };

    applyTableState = ({
        baseData = this.state.baseData,
        nomeFiltro = this.state.nomeFiltro,
        sortBy = this.state.sortBy
    } = {}) => {
        const filtro = `${nomeFiltro || ''}`.trim().toLowerCase();
        const filteredData = filtro
            ? baseData.filter(item => `${item.nome || ''}`.toLowerCase().includes(filtro))
            : [...baseData];

        const datatable = this.sortPatients(filteredData, sortBy);

        this.setState({
            baseData,
            datatable,
            nomeFiltro,
            sortBy,
            currentPage: 1
        });
    };

    filter() {
        this.applyTableState();
    }

    fetch = async () => {
        if (!canAccessPatients(acesso)) {
            window.open('/', "_self");
            return;
        }

        this.setState({ loading: true });

        partnersService.getList().then(response => {
            if (response.status === 201) {
                this.setState({ partnerList: filterNamedPartners(response.data || []) });
                return;
            }

            this.setState({
                show: true,
                message: 'Nao foram encontrados profissionais para vinculo.'
            });
        }).catch((error) => {
            this.showError(error, 'Nao foi possivel carregar os profissionais para vinculo.');
        });

        const request = readOnlyMode ? patientsService.getAssigned() : patientsService.get();

        request.then(response => {
            if (response.status !== 201) {
                return;
            }

            this.applyTableState({
                baseData: filterNamedPatients(response.data || []),
                nomeFiltro: ''
            });
        }).catch((error) => {
            this.showError(error, 'Nao foi possivel carregar os pacientes.');
        }).finally(() => {
            this.setState({ loading: false });
        });
    };

    openModal() {
        this.setState({
            idPacientes: '',
            nome: '',
            datanascimento: '',
            rg: '',
            cpf: '',
            idParceiro: '',
            entrada: '',
            saida: '',
            modalShow: true
        });
    }

    cancelEdit() {
        try {
            this.setState({
                idPacientes: '',
                nome: '',
                datanascimento: '',
                rg: '',
                cpf: '',
                idParceiro: '',
                entrada: '',
                saida: '',
                modalShow: false,
                saving: false
            });
        } catch (error) {
            this.showError(error);
        }
    }

    saveOrUpdate() {
        try {
            const {
                idPacientes,
                nome,
                datanascimento,
                rg,
                cpf,
                idParceiro,
                entrada,
                saida
            } = this.state;

            if (!normalizeText(nome)) {
                throw new Error('Nome do paciente obrigatorio.');
            }

            if (!idParceiro) {
                throw new Error('Profissional responsavel obrigatorio.');
            }

            const duplicateMessage = getDuplicatePatientMessage(this.state.baseData, {
                idPacientes,
                nome,
                datanascimento,
                rg,
                cpf
            });

            if (duplicateMessage) {
                throw new Error(duplicateMessage);
            }

            const body = {
                idPacientes,
                nome: normalizeText(nome),
                datanascimento,
                rg: normalizeText(rg),
                cpf: normalizeText(cpf),
                idParceiro,
                status: 'Y',
                entrada,
                saida
            };

            this.setState({ saving: true });
            const request = idPacientes ? patientsService.patch(body, idPacientes) : patientsService.post(body);

            request.then(response => {
                this.setState({ show: true, message: response.data, modalShow: false });
                setTimeout(() => { this.setState({ show: false }); }, 7000);
                this.cancelEdit();
                this.fetch();
            }).catch(error => {
                this.showError(error, idPacientes ? 'Nao foi possivel atualizar o paciente.' : 'Nao foi possivel inserir o paciente.');
                this.setState({ saving: false });
            });
        } catch (error) {
            this.showError(error);
        }
    }

    changeUserStatus(id, status) {
        try {
            patientsService.patchStatus({ Status: status ? 'Y' : 'N', Id: id }, id).then(response => {
                const updatedBaseData = this.state.baseData.map((item) => (
                    Number(item.idPacientes) === Number(id)
                        ? { ...item, status: status ? 'Y' : 'N' }
                        : item
                ));

                this.setState({ show: true, message: response.data });
                this.applyTableState({ baseData: updatedBaseData });
                setTimeout(() => { this.setState({ show: false }); }, 7000);
            }).catch(error => {
                this.showError(error, 'Nao foi possivel alterar o status do paciente.');
            });
        } catch (error) {
            this.showError(error);
        }
    }

    returnPartnerName(id) {
        const partner = this.state.partnerList.find(item => Number(item.idUsuario) === Number(id));
        return partner ? `${partner.Nome} ${partner.Sobrenome}`.trim() : '-';
    }

    edit(data) {
        try {
            this.setState({
                idPacientes: data.idPacientes,
                nome: data.nome,
                datanascimento: formatDateForInput(data.datanascimento),
                rg: data.rg,
                cpf: data.cpf,
                idParceiro: String(data.idParceiro || ''),
                entrada: data.entrada,
                saida: data.saida,
                modalShow: true
            });
        } catch (error) {
            this.showError(error);
        }
    }

    renderManageModal() {
        return (
            <Modal className="portal-modal" show={this.state.modalShow} onHide={() => { this.cancelEdit(); }}>
                <Modal.Header closeButton>
                    <Modal.Title>Paciente</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="portal-form-grid two-columns">
                        <Form.Group>
                            <Form.Label>ID</Form.Label>
                            <Form.Control readOnly value={this.state.idPacientes} type="text" placeholder="ID" />
                        </Form.Group>
                        <Form.Group>
                            <Form.Label>Profissional responsavel</Form.Label>
                            <Form.Select value={this.state.idParceiro} onChange={item => this.setState({ idParceiro: item.target.value })}>
                                <option value="">Selecione o profissional</option>
                                {this.state.partnerList.map((partner) => (
                                    <option key={partner.idUsuario} value={partner.idUsuario}>
                                        {`${partner.Nome} ${partner.Sobrenome}`}
                                    </option>
                                ))}
                            </Form.Select>
                        </Form.Group>
                    </div>
                    <div className="portal-form-grid two-columns">
                        <Form.Group>
                            <Form.Label>Nome</Form.Label>
                            <Form.Control value={this.state.nome} type="text" placeholder="Nome" onChange={item => this.setState({ nome: item.target.value })} />
                        </Form.Group>
                        <Form.Group>
                            <Form.Label>Data de nascimento</Form.Label>
                            <Form.Control pattern="[0-9]{4}-[0-9]{2}-[0-9]{2}" value={this.state.datanascimento} type="date" onChange={item => this.setState({ datanascimento: item.target.value })} />
                        </Form.Group>
                    </div>
                    <div className="portal-form-grid two-columns">
                        <Form.Group>
                            <Form.Label>RG</Form.Label>
                            <Form.Control value={this.state.rg} type="text" placeholder="RG" onChange={item => this.setState({ rg: item.target.value })} />
                        </Form.Group>
                        <Form.Group>
                            <Form.Label>CPF</Form.Label>
                            <Form.Control value={this.state.cpf} type="text" placeholder="CPF" onChange={item => this.setState({ cpf: item.target.value })} />
                        </Form.Group>
                    </div>
                    <div className="portal-form-grid two-columns">
                        <Form.Group>
                            <Form.Label>Entrada</Form.Label>
                            <Form.Control value={this.state.entrada} type="time" placeholder="Entrada" onChange={item => this.setState({ entrada: item.target.value })} />
                        </Form.Group>
                        <Form.Group>
                            <Form.Label>Saida</Form.Label>
                            <Form.Control value={this.state.saida} type="time" placeholder="Saida" onChange={item => this.setState({ saida: item.target.value })} />
                        </Form.Group>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => { this.cancelEdit(); }} disabled={this.state.saving}>
                        Fechar
                    </Button>
                    <Button variant="success" onClick={() => { this.saveOrUpdate(); }} disabled={this.state.saving}>
                        {this.state.saving ? 'Salvando...' : 'Salvar'}
                    </Button>
                </Modal.Footer>
            </Modal>
        );
    }

    render() {
        const { datatable, currentPage, postPerPage, loading } = this.state;
        const totalPages = Math.max(1, Math.ceil(datatable.length / postPerPage));
        const safePage = Math.min(Math.max(currentPage, 1), totalPages);
        const indexOfLastPost = safePage * postPerPage;
        const indexOfFirstPost = indexOfLastPost - postPerPage;
        const currentData = datatable.slice(indexOfFirstPost, indexOfLastPost);
        const activePatients = datatable.filter((item) => item.status === 'Y').length;

        return (
            <Container fluid className="portal-shell">
                <div className="portal-alert">
                    <Alert show={this.state.show} variant="info" dismissible onClose={() => this.setState({ show: false })}>
                        {this.state.message}
                    </Alert>
                </div>

                <div className="page-header">
                    <div>
                        <h2 className="page-title">Pacientes</h2>
                        <p className="page-subtitle">
                            {readOnlyMode
                                ? 'Consulta rapida da carteira vinculada ao atendimento.'
                                : 'Cadastros, historico de atendimento e vinculo com profissionais.'}
                        </p>
                    </div>
                </div>

                <div className="portal-page-grid">
                    <div className="portal-page-main">
                        <div className="portal-card portal-section">
                            <div className="portal-card-header">
                                <div className="portal-card-copy">
                                    <span className="portal-card-kicker">Busca</span>
                                    <h3 className="portal-card-title">Lista de pacientes</h3>
                                    <p className="portal-card-subtitle">
                                        Filtre pelo nome para encontrar rapidamente o cadastro desejado.
                                    </p>
                                </div>
                                <div className="portal-table-actions">
                                    <Button as={Link} to="/" variant="secondary">
                                        Voltar
                                    </Button>
                                    {readOnlyMode ? null : (
                                        <Button variant="success" onClick={() => { this.openModal(); }}>
                                            Adicionar
                                        </Button>
                                    )}
                                </div>
                            </div>

                            <div className="portal-filter-bar">
                                <Form.Control
                                    type="text"
                                    placeholder="Nome do paciente"
                                    value={this.state.nomeFiltro}
                                    onChange={item => this.setState({ nomeFiltro: item.target.value })}
                                />
                                <Form.Select
                                    className="portal-select-control"
                                    value={this.state.sortBy}
                                    onChange={(event) => this.applyTableState({ sortBy: event.target.value })}
                                >
                                    <option value="nome-asc">Ordenar: Nome A-Z</option>
                                    <option value="nome-desc">Ordenar: Nome Z-A</option>
                                    <option value="id-desc">Ordenar: ID mais recente</option>
                                    <option value="id-asc">Ordenar: ID mais antigo</option>
                                </Form.Select>
                                <div className="portal-table-actions">
                                    <Button variant="success" onClick={() => { this.filter(); }}>
                                        Filtrar
                                    </Button>
                                    <Button variant="danger" onClick={() => { this.applyTableState({ nomeFiltro: '' }); }}>
                                        Limpar
                                    </Button>
                                </div>
                            </div>

                            {loading ? (
                                <PortalSpinner
                                    block
                                    label="Carregando pacientes"
                                    description="Buscando cadastros e vinculos desta unidade."
                                />
                            ) : (
                                <>
                                    <PortalTableScroller>
                                        <Table className="portal-table">
                                            <thead>
                                                <tr>
                                                    <th>ID</th>
                                                    <th>Nome</th>
                                                    <th>Data Nascimento</th>
                                                    <th>RG</th>
                                                    <th>CPF</th>
                                                    <th>Parceiro</th>
                                                    <th>Status</th>
                                                    <th>Entrada</th>
                                                    <th>Saida</th>
                                                    {readOnlyMode ? null : <th>Habilitar</th>}
                                                    {readOnlyMode ? null : <th>Acoes</th>}
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {currentData.length ? currentData.map(item => (
                                                    <tr key={item.idPacientes}>
                                                        <td className="portal-data-strong">{item.idPacientes}</td>
                                                        <td>{item.nome}</td>
                                                        <td>{formatDateDisplay(item.datanascimento)}</td>
                                                        <td>{item.rg || '-'}</td>
                                                        <td>{item.cpf || '-'}</td>
                                                        <td>{this.returnPartnerName(item.idParceiro)}</td>
                                                        <td>{item.status === 'Y' ? 'Ativo' : 'Inativo'}</td>
                                                        <td>{item.entrada || '-'}</td>
                                                        <td>{item.saida || '-'}</td>
                                                        {readOnlyMode ? null : (
                                                            <td>
                                                                <Form.Check
                                                                    type="switch"
                                                                    id={`patient-status-${item.idPacientes}`}
                                                                    checked={item.status === 'Y'}
                                                                    onChange={event => this.changeUserStatus(item.idPacientes, event.target.checked)}
                                                                />
                                                            </td>
                                                        )}
                                                        {readOnlyMode ? null : (
                                                            <td>
                                                                <Button variant="primary" onClick={() => { this.edit(item); }}>
                                                                    Editar
                                                                </Button>
                                                            </td>
                                                        )}
                                                    </tr>
                                                )) : (
                                                    <tr>
                                                        <td colSpan={readOnlyMode ? 9 : 11}>
                                                            <div className="portal-empty-state">Nenhum paciente encontrado.</div>
                                                        </td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </Table>
                                    </PortalTableScroller>

                                    <div className="portal-pagination">
                                        <PortalPagination
                                            currentPage={safePage}
                                            onPageChange={(pageNumber) => this.setState({ currentPage: pageNumber })}
                                            totalPages={totalPages}
                                        />
                                    </div>
                                </>
                            )}
                        </div>
                    </div>

                    <div className="portal-page-side">
                        <div className="portal-card portal-section">
                            <span className="portal-card-kicker">Visao geral</span>
                            <div className="portal-overview-grid">
                                <div className="portal-overview-item">
                                    <strong>{loading ? '--' : datatable.length}</strong>
                                    <span>{readOnlyMode ? 'pacientes na sua carteira' : 'pacientes cadastrados'}</span>
                                </div>
                                <div className="portal-overview-item">
                                    <strong>{loading ? '--' : activePatients}</strong>
                                    <span>pacientes ativos no fluxo</span>
                                </div>
                            </div>
                        </div>

                        <div className="portal-card portal-section">
                            <span className="portal-card-kicker">Orientacao</span>
                            <div className="portal-note-list">
                                <div className="portal-note">
                                    <strong>{readOnlyMode ? 'Modo leitura' : 'Gestao ativa'}</strong>
                                    <span>
                                        {readOnlyMode
                                            ? 'Este perfil consulta somente os pacientes vinculados ao proprio atendimento.'
                                            : 'Use os filtros e o painel lateral para navegar pelos cadastros com mais rapidez.'}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {readOnlyMode ? null : this.renderManageModal()}
            </Container>
        );
    }
}

export default PartientManage;
